/**
 * AdService.js - Centralized Ad Abstraction Layer
 * Provider-agnostic advertising service for Nethaji Prime OTT
 */

const AD_CONFIG = {
  IS_PRODUCTION: window.location.hostname !== 'localhost',
  ADSENSE_CLIENT_ID: process.env.REACT_APP_ADSENSE_CLIENT_ID || 'ca-pub-XXXXXXXXXXXXXXXX',
  FREQUENCY_CAP_MINUTES: 30,
  PLACEMENTS: { banner: true, preroll: true, lband: true, midroll: false, postroll: false },
  EXCLUDE_PREMIUM: true,
  PROVIDERS: ['direct', 'adsense'],
};

class AdServiceClass {
  constructor() {
    this.sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    this.shownAds = {};
    this.globalEnabled = null;
    this.config = AD_CONFIG;
    this.loadFrequencyCaps();
  }

  shouldShowAds(isPaidUser = false) {
    if (this.config.EXCLUDE_PREMIUM && isPaidUser) return false;
    if (this.globalEnabled === false) return false;
    return true;
  }

  // Direct-sold ads are chosen by a lottery weighted on monthly_rate, so a
  // Rs.15,000/mo campaign outdraws a Rs.5,000/mo one 3:1. Selection used to be
  // "first in the list" (i.e. newest-created always won) or a flat random pick,
  // and monthly_rate was stored but never read - a free house ad and a paid
  // campaign had identical odds.
  //
  // House ads (rate 0) can never win a rate-weighted draw, so they are eligible
  // only when no paid ad is; otherwise one house ad would starve paid inventory.
  pickWeightedAd(candidates = []) {
    const pool = candidates.filter(a => !this.isFrequencyCapped(a.id));
    if (!pool.length) return null;
    const paid = pool.filter(a => Number(a.monthly_rate) > 0);
    if (!paid.length) return pool[Math.floor(Math.random() * pool.length)];
    const total = paid.reduce((s, a) => s + Number(a.monthly_rate), 0);
    let roll = Math.random() * total;
    for (const a of paid) {
      roll -= Number(a.monthly_rate);
      if (roll <= 0) return a;
    }
    return paid[paid.length - 1];
  }

  isFrequencyCapped(adId) {
    const lastShown = this.shownAds[adId];
    if (!lastShown) return false;
    return (Date.now() - lastShown) / 60000 < this.config.FREQUENCY_CAP_MINUTES;
  }

  recordAdShown(adId) {
    this.shownAds[adId] = Date.now();
    try {
      const stored = JSON.parse(localStorage.getItem('np_ad_caps') || '{}');
      stored[adId] = Date.now();
      localStorage.setItem('np_ad_caps', JSON.stringify(stored));
    } catch {}
  }

  loadFrequencyCaps() {
    try {
      const stored = JSON.parse(localStorage.getItem('np_ad_caps') || '{}');
      const cap = this.config.FREQUENCY_CAP_MINUTES * 60000;
      Object.entries(stored).forEach(([id, time]) => {
        if (Date.now() - time < cap) this.shownAds[id] = time;
      });
    } catch {}
  }

  async fetchGlobalConfig(backendUrl) {
    try {
      const r = await fetch(backendUrl + '/api/ad-config');
      const d = await r.json();
      this.globalEnabled = d.ads_enabled !== false;
      // The admin-set cap and premium rule were fetched here and then thrown
      // away, so both Ad Manager controls did nothing and a hardcoded 30min
      // was used instead. app_settings stores values as strings, so coerce.
      const cap = Number(d.ads_frequency_cap);
      if (Number.isFinite(cap) && cap >= 0) this.config.FREQUENCY_CAP_MINUTES = cap;
      if (d.ads_exclude_premium !== undefined && d.ads_exclude_premium !== null) {
        this.config.EXCLUDE_PREMIUM = d.ads_exclude_premium === true || d.ads_exclude_premium === 'true';
      }
      return d;
    } catch {
      this.globalEnabled = true;
      return { ads_enabled: true };
    }
  }

  async trackEvent(backendUrl, eventData) {
    try {
      const ua = navigator.userAgent;
      const isMobile = /Android|iPhone|iPad/i.test(ua);
      const platform = /Android/i.test(ua) ? 'android' : /iPhone|iPad/i.test(ua) ? 'ios' : 'web';
      const lang = navigator.language || 'en-IN';
      const country = lang.includes('-') ? lang.split('-')[1] : 'IN';
      await fetch(backendUrl + '/api/ad-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...eventData, session_id: this.sessionId, country, device_type: isMobile ? 'mobile' : 'desktop', platform, ad_network: eventData.ad_network || 'direct', currency: 'INR' })
      });
    } catch(e) { console.warn('[AdService] Event tracking failed:', e.message); }
  }

  isPlacementEnabled(placement) { return this.config.PLACEMENTS[placement] !== false; }
  getSessionId() { return this.sessionId; }
}

const AdService = new AdServiceClass();
export default AdService;
export { AD_CONFIG };
